import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { Pause, Play } from 'lucide-react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { cn } from '@/lib/utils';
import { useEntrada } from '@/components/auth/EntradaDoCadeado';
import { BrandMark } from '@/components/brand/BrandMark';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import {
  CARACTERES,
  DEGRAU,
  DURACAO,
  PADRAO,
} from '@/components/ui/DevClubLogo';
import styles from './RotatingBrand.module.css';

type BrandKey = 'grupo' | 'devclub' | 'works' | 'iaclub';
type MarkKey = 'devclub' | 'works' | 'iaclub';

/** Nome em partes, como o "Grupo" + "Club" do login; `true` = cor de acento. */
const BRANDS: Record<
  BrandKey,
  { mark: MarkKey; parts: [string, boolean][]; accent: string }
> = {
  grupo: {
    mark: 'devclub',
    parts: [
      ['Grupo', false],
      ['Club', true],
    ],
    accent: 'hsl(var(--primary-texto))',
  },
  devclub: {
    mark: 'devclub',
    parts: [
      ['Dev', false],
      ['Club', true],
    ],
    accent: 'var(--brand-devclub-text)',
  },
  works: {
    mark: 'works',
    parts: [
      ['Works', false],
      [' AI', true],
    ],
    accent: 'var(--brand-works-text)',
  },
  iaclub: {
    mark: 'iaclub',
    parts: [
      ['IA', true],
      ['Club', false],
    ],
    accent: 'var(--brand-ia-text)',
  },
};
const ORDER = Object.keys(BRANDS) as BrandKey[];
const INTRO: BrandKey = 'grupo';
const LOOP: BrandKey[] = ['devclub', 'works', 'iaclub'];
const INTRO_DELAY_MS = 800;
const FIRST_HOLD_MS = 2400;
const HOLD_MS = 4000;
/** Com tudo coberto de bits, a marca de baixo e o nome trocam aqui. */
const SWAP_AT_S = 0.7;
/** Grade do símbolo do DevClub com 1 módulo de margem (viewBox -1 -1 15 15). */
const GRADE = 15;
/** Contém todo texto que a marca pode mostrar (WCAG 2.5.3) e não muda. */
export const BRAND_LABEL =
  'GrupoClub (DevClub, Works AI e IAClub) — início da área de membros';

/**
 * Memória da sessão: a abertura toca uma vez por carga. Só a Home gira;
 * ao voltar para ela, a rotação continua de onde parou. A pausa no menu
 * da conta fica guardada no navegador (WCAG 2.2.2: parar o que se move).
 */
export const useBrandRotation = create<{
  introSeen: boolean;
  current: BrandKey;
  paused: boolean;
  setPaused: (paused: boolean) => void;
}>()(
  persist(
    (set) => ({
      introSeen: false,
      current: INTRO,
      paused: false,
      setPaused: (paused) => set({ paused }),
    }),
    {
      name: 'grupoclub-marca-pausada',
      partialize: (state) => ({ paused: state.paused }),
    },
  ),
);

/** Marca da formação; páginas genéricas mostram apenas o símbolo do DevClub. */
export function StaticBrand({
  brand,
  className,
}: {
  brand?: MarkKey;
  className?: string;
}) {
  const selected = BRANDS[brand ?? 'devclub'];
  const name = selected.parts.map(([text]) => text).join('');
  return (
    <Link
      to="/home"
      aria-label={`${name} — início da área de membros`}
      data-marca={selected.mark}
      className={cn(styles.brand, className)}
    >
      <span className={styles.slot} aria-hidden="true">
        <BrandMark
          brand={selected.mark}
          variant="symbol"
          decorative
          style={{ width: '100%', height: '100%' }}
        />
      </span>
      {brand && (
        <span className={styles.names} aria-hidden="true">
          <span>
            {selected.parts.map(([text, accent]) => (
              <span
                key={text}
                style={accent ? { color: selected.accent } : undefined}
              >
                {text}
              </span>
            ))}
          </span>
        </span>
      )}
    </Link>
  );
}

/** O useReducedMotion do framer-motion lê uma vez e não acompanha a troca. */
function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduce(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return reduce;
}

const draw = () => CARACTERES[(Math.random() * CARACTERES.length) | 0];
/** Onda na diagonal do DevClubLogo: entra no embaralho, depois assenta. */
const wave = (d: number): [number, number] => [
  d * 0.35 + Math.random() * 0.1,
  0.95 + d * 0.6 + Math.random() * 0.15,
];
const cellsOf = (g: SVGGElement | null) =>
  Array.from(g?.children ?? []) as SVGElement[];

function writeName(stage: HTMLElement | null, key: BrandKey, mixing = false) {
  if (!stage) return;
  stage.style.setProperty('--acento', BRANDS[key].accent);
  stage.replaceChildren(
    ...BRANDS[key].parts.flatMap(([text, accent]) =>
      Array.from(text, (ch) => {
        const letter = document.createElement('span');
        letter.dataset.letra = ch;
        if (accent) letter.className = styles.accent;
        letter.textContent = mixing && ch !== ' ' ? draw() : ch;
        if (mixing && ch !== ' ') letter.dataset.codigo = '';
        return letter;
      }),
    ),
  );
}

/**
 * Marca do topo da Home. Abre com a animação do logo do login
 * (DC + "GrupoClub") e depois alterna DevClub, Works AI e IAClub. Para com o
 * mouse em cima, no foco do teclado e com a aba escondida; fica parada com
 * movimento reduzido ou quando a pessoa pausa no menu da conta. A largura é a
 * do nome mais largo, então nada ao lado se mexe.
 */
export function RotatingBrand({
  className,
  animated = true,
}: {
  className?: string;
  /** false no editor de aparência (StudentLayout em editMode). */
  animated?: boolean;
}) {
  const reduce = usePrefersReducedMotion();
  const covered = useEntrada((s) => s.pedido !== null);
  const paused = useBrandRotation((s) => s.paused);
  const [shown, setShown] = useState(() => useBrandRotation.getState().current);
  const enabled = animated && !reduce && !covered && !paused;

  const slot = useRef<HTMLSpanElement>(null);
  const modules = useRef<SVGGElement>(null);
  const veil = useRef<SVGGElement>(null);
  const stage = useRef<HTMLSpanElement>(null);
  const timer = useRef<number>();
  const frame = useRef<number>();
  const running = useRef(false);
  const holds = useRef(new Set<'hover' | 'focus' | 'hidden'>());
  /** Foco vindo de clique ou toque não pausa; só o do teclado. */
  const pointerFocus = useRef(false);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  /** Repouso: o desenho de cada marca, sem bits e com o nome inteiro. */
  const paintRest = useCallback((key: BrandKey) => {
    if (slot.current) slot.current.dataset.marca = BRANDS[key].mark;
    cellsOf(modules.current).forEach((c, k) => {
      c.style.opacity = PADRAO[k] === '1' ? '1' : '0';
    });
    cellsOf(veil.current).forEach((c) => {
      c.style.opacity = '0';
    });
    writeName(stage.current, key);
  }, []);
  useLayoutEffect(() => paintRest(shown), [shown, paintRest]);

  const run = useCallback((from: BrandKey, to: BrandKey, done: () => void) => {
    const dc = cellsOf(modules.current);
    const bits = cellsOf(veil.current);
    if (!slot.current || !stage.current) return;
    running.current = true;
    const sameMark = BRANDS[from].mark === BRANDS[to].mark;
    const dcOut = BRANDS[from].mark === 'devclub';
    const dcIn = BRANDS[to].mark === 'devclub';
    const dcT = dc.map((_, k) => wave(((k % 13) + ((k / 13) | 0)) / 24));
    const bitT = bits.map((_, k) =>
      wave(((k % GRADE) + ((k / GRADE) | 0)) / (2 * GRADE - 2)),
    );
    const t0 = performance.now();
    let last = -1;
    let swapped = false;
    const step = (now: number) => {
      const s = (now - t0) / 1000;
      if (!swapped && s >= SWAP_AT_S) {
        swapped = true;
        if (slot.current) slot.current.dataset.marca = BRANDS[to].mark;
        writeName(stage.current, to, true);
      }
      const degrau = Math.floor(s / DEGRAU);
      if (degrau !== last) {
        last = degrau;
        const mixing = ([a, b]: [number, number]) => s >= a && s < b;
        if (sameMark || (swapped ? dcIn : dcOut))
          dc.forEach((c, k) => {
            const on = mixing(dcT[k]) ? Math.random() < 0.5 : PADRAO[k] === '1';
            c.style.opacity = on ? '1' : '0';
          });
        if (!sameMark)
          bits.forEach((c, k) => {
            c.style.opacity =
              mixing(bitT[k]) && Math.random() < 0.5 ? '1' : '0';
          });
        stage.current
          ?.querySelectorAll<HTMLElement>('[data-letra]')
          .forEach((l, i) => {
            const ch = l.dataset.letra ?? '';
            if (ch === ' ') return;
            const on = swapped ? s < 0.8 + i * 0.09 : s >= i * 0.03;
            l.textContent = on ? draw() : ch;
            if (on) l.dataset.codigo = '';
            else delete l.dataset.codigo;
          });
      }
      if (s < DURACAO) {
        frame.current = requestAnimationFrame(step);
        return;
      }
      frame.current = undefined;
      running.current = false;
      useBrandRotation.setState({ current: to });
      setShown(to);
      paintRest(to);
      done();
    };
    frame.current = requestAnimationFrame(step);
  }, [paintRest]);

  const schedule = useCallback(
    (ms: number) => {
      window.clearTimeout(timer.current);
      const { introSeen } = useBrandRotation.getState();
      if (!enabledRef.current || !introSeen || running.current) return;
      if (holds.current.size) return;
      timer.current = window.setTimeout(() => {
        const { current } = useBrandRotation.getState();
        // 'grupo' não está no LOOP: indexOf -1 leva ao DevClub
        const next = LOOP[(LOOP.indexOf(current) + 1) % LOOP.length];
        run(current, next, () => schedule(HOLD_MS));
      }, ms);
    },
    [run],
  );

  const hold = (why: 'hover' | 'focus' | 'hidden') => {
    holds.current.add(why);
    window.clearTimeout(timer.current); // a troca em curso termina (1,8 s)
  };
  const release = (why: 'hover' | 'focus' | 'hidden') => {
    holds.current.delete(why);
    schedule(HOLD_MS); // retoma com uma espera inteira
  };

  useEffect(() => {
    const onVisibility = () =>
      document.hidden ? hold('hidden') : release('hidden');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []); // hold/release só mexem em refs e no schedule estável

  useEffect(() => {
    if (!enabled) return; // parado: só o repouso, nenhum relógio
    let alive = true;
    const start = () => {
      if (!alive || running.current) return;
      if (useBrandRotation.getState().introSeen) return schedule(HOLD_MS);
      // marca ao COMEÇAR: no StrictMode a 1ª montagem é descartada antes disso
      useBrandRotation.setState({ introSeen: true });
      run(INTRO, INTRO, () => schedule(FIRST_HOLD_MS));
    };
    const fonts = document.fonts?.ready ?? Promise.resolve();
    const wait = window.setTimeout(
      () => void fonts.then(start),
      INTRO_DELAY_MS,
    );
    return () => {
      alive = false;
      window.clearTimeout(wait);
      window.clearTimeout(timer.current);
      if (frame.current) cancelAnimationFrame(frame.current);
      frame.current = undefined;
      running.current = false;
      paintRest(useBrandRotation.getState().current);
    };
  }, [enabled, run, schedule, paintRest]);

  return (
    <Link
      to="/home"
      aria-label={BRAND_LABEL}
      data-marca={shown}
      className={cn(styles.brand, className)}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') hold('hover');
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') release('hover');
      }}
      onPointerDown={() => {
        pointerFocus.current = true;
      }}
      onFocus={() => {
        if (!pointerFocus.current) hold('focus');
        pointerFocus.current = false;
      }}
      onBlur={() => release('focus')}
    >
      <span ref={slot} className={styles.slot} aria-hidden="true">
        <svg
          className={cn(styles.layer, styles.devclub)}
          viewBox="-1 -1 15 15"
          shapeRendering="crispEdges"
        >
          <g ref={modules}>
            {Array.from(PADRAO, (bit, k) => (
              <rect
                key={k}
                x={k % 13}
                y={(k / 13) | 0}
                width={1}
                height={1}
                style={{ opacity: bit === '1' ? 1 : 0 }}
              />
            ))}
          </g>
        </svg>
        {(['works', 'iaclub'] as const).map((mark) => (
          <span key={mark} className={cn(styles.layer, styles[mark])}>
            <BrandMark
              brand={mark}
              variant="symbol"
              decorative
              style={{ width: '100%', height: '100%' }}
            />
          </span>
        ))}
        <svg
          className={styles.veil}
          viewBox="-1 -1 15 15"
          shapeRendering="crispEdges"
        >
          <g ref={veil}>
            {Array.from({ length: GRADE * GRADE }, (_, k) => (
              <rect
                key={k}
                x={(k % GRADE) - 1}
                y={((k / GRADE) | 0) - 1}
                width={1}
                height={1}
                style={{ opacity: 0 }}
              />
            ))}
          </g>
        </svg>
      </span>
      <span className={styles.names} aria-hidden="true">
        {ORDER.map((key) => (
          <span key={key} className={styles.measure}>
            {BRANDS[key].parts.map(([text]) => text).join('')}
          </span>
        ))}
        <span ref={stage} className={styles.stage} />
      </span>
    </Link>
  );
}

/** Item do menu da conta que para ou retoma a marca do topo. */
export function BrandPauseMenuItem({
  className,
  iconClassName,
}: {
  className?: string;
  iconClassName?: string;
}) {
  const paused = useBrandRotation((s) => s.paused);
  const setPaused = useBrandRotation((s) => s.setPaused);
  const Icon = paused ? Play : Pause;
  return (
    <DropdownMenuItem className={className} onSelect={() => setPaused(!paused)}>
      <Icon size={17} aria-hidden="true" className={iconClassName} />
      {paused ? 'Retomar animação do logo' : 'Pausar animação do logo'}
    </DropdownMenuItem>
  );
}
